import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { post, put } from '~lib/api/client';
import { fetcher } from '~lib/api/fetch';
import { Nullable } from '~types/common';
import { JobdomComment, JobdomDetailV1, JobdomPost, JobdomPostV1 } from '~types/jobdom';

import QueryKeys from '../queryKeys';

/**
 * legacy api의 인터페이스
 */
interface PostsResponse {
  count: number;
  board: JobdomPostV1[];
}

interface NewPostsResponse extends Pick<PostsResponse, 'count'> {
  rows: JobdomPost[];
}

export type ReactionType = 'plus' | 'minus';
type OnSuccess = () => void;

interface UpdateLikeParams {
  jobdomNumber: number;
  type: ReactionType;
}

interface UpdateCommentLikeParams extends Pick<UpdateLikeParams, 'type'> {
  commentNumber: number;
}

interface UseGetJobdomQueryParams {
  page?: number;
  CATEGORY_ID?: Nullable<string>;
  PK_NM_HASH?: Nullable<string>;
  AVG_SALARY_FINAL?: number;
  PRSN_BASE?: number;
  KEYWORD?: Nullable<string>;
}

/**
 * 잡담리스트 조회
 * @param param0
 * @returns
 */
export function useGetJobdomQuery({
  page = 1,
  CATEGORY_ID,
  PK_NM_HASH,
  AVG_SALARY_FINAL,
  PRSN_BASE,
  KEYWORD,
}: UseGetJobdomQueryParams) {
  const payload: any = {};

  if (CATEGORY_ID) {
    payload['category'] = ['CATEGORY_ID', CATEGORY_ID];
  }
  if (PK_NM_HASH) {
    payload['PK_NM_HASH'] = ['PK_NM_HASH', PK_NM_HASH];
  }
  if (AVG_SALARY_FINAL) {
    payload['AVG_SALARY_FINAL'] = ['AVG_SALARY_FINAL', AVG_SALARY_FINAL];
  }
  if (PRSN_BASE) {
    payload['PRSN_BASE'] = ['PRSN_BASE', PRSN_BASE];
  }
  if (KEYWORD) {
    payload['KEYWORD'] = KEYWORD;
  }

  return useQuery<AxiosResponse<PostsResponse>>(
    [QueryKeys.jobdom, 'list', page, KEYWORD, PK_NM_HASH, CATEGORY_ID],
    () => post<AxiosResponse<PostsResponse>>(`/api/jobdom/multiFilter/${page}/10`, payload),
    { enabled: !!page, refetchOnMount: true },
  );
}

/**
 * 잡담 상세보기
 * @param jobdomId
 * @returns
 */
export function useGetJobdomDetailQuery(jobdomId: number, onError?: (err: AxiosResponse) => void) {
  return useQuery<JobdomDetailV1, AxiosResponse>(
    [QueryKeys.jobdom, 'detail', jobdomId],
    () => fetcher<JobdomDetailV1>(`/api/jobdom/content/${jobdomId}`),
    {
      enabled: !!jobdomId,
      refetchOnMount: true,
      onError,
    },
  );
}

/**
 * 잡담 댓글조회
 * @param jobdomId
 * @returns
 */
export function useGetJobdomCommentsQuery(jobdomId: number) {
  return useQuery<JobdomComment[]>(
    [QueryKeys.jobdom, 'comments', jobdomId],
    () => fetcher(`/api/jobdomComments/fetch/${jobdomId}/1/100`),
    {
      enabled: !!jobdomId,
      refetchOnMount: true,
    },
  );
}

/**
 * 기업의 hash을 이용해 최대 3개만 기업의 잡담노출
 * @param companyHash
 * @returns
 */
export function useGetJobdomByCompanyQuery(companyHash: string) {
  return useQuery<NewPostsResponse>(
    [QueryKeys.jobdom, 'company', companyHash],
    () => fetcher<NewPostsResponse>(`/api/company/${companyHash}/posts`),
    {
      enabled: !!companyHash,
    },
  );
}

/**
 * 잡담 "좋아요" 업데이트
 * @param onSuccess
 * @returns
 */
export function useJobdomLikeMutation(onSuccess?: OnSuccess) {
  return useMutation(
    ({ jobdomNumber, type }: UpdateLikeParams) =>
      put('/api/jobdom/like', { JOBDOM_NO: jobdomNumber, type }),
    {
      onSuccess,
    },
  );
}

/**
 * 잡담 "싫어요" 업데이트
 * @param onSuccess
 * @returns
 */
export function useJobdomDislikeMutation(onSuccess?: OnSuccess) {
  return useMutation(
    ({ jobdomNumber, type }: UpdateLikeParams) =>
      put('/api/jobdom/dislike', { JOBDOM_NO: jobdomNumber, type }),
    {
      onSuccess,
    },
  );
}

/**
 * 잡담 코멘트 "좋아요" 업데이트
 * @param onSuccess
 * @returns
 */
export function useJobdomCommentLikeMutation(onSuccess?: OnSuccess) {
  return useMutation(
    ({ commentNumber, type }: UpdateCommentLikeParams) =>
      put('/api/jobdomComments/like', { COMMENT_NO: commentNumber, type }),
    {
      onSuccess,
    },
  );
}

/**
 * 잡담 코멘트 "싫어요" 업데이트
 * @param onSuccess
 * @returns
 */
export function useJobdomCommentDislikeMutation(onSuccess?: OnSuccess) {
  return useMutation(
    ({ commentNumber, type }: UpdateCommentLikeParams) =>
      put('/api/jobdomComments/dislike', { COMMENT_NO: commentNumber, type }),
    {
      onSuccess,
    },
  );
}
